/**
 * Backloader – Transport Association partner jobs.
 * Plan return legs by finding jobs from trusted partners (mock data).
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Package,
  MapPin,
  Calendar,
  Truck,
  Building2,
  Navigation,
  Plus,
  Mail,
  Phone,
  X,
  Send,
} from 'lucide-react';
import { Map as MapComponent, MapControls, MapMarker, MarkerContent, MarkerTooltip, MarkerPopup, useMap } from '@/components/ui/map';
import DataTable from '../components/DataTable';
import {
  MOCK_BACKLOADER_JOBS,
  MOCK_PARTNER_COMPANIES,
  MOCK_LOCATIONS,
  type BackloaderJob,
} from '../data/backloaderMockData';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { Button } from '@/components/ui/button';

const KM_TO_MILES = 0.621371;

/** Suggestion row returned by the geocode autocomplete endpoint. */
interface GeocodeSuggestion {
  address?: string;
  postcode?: string;
  lat: string | number;
  lon: string | number;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export interface PostedJobEntry {
  id: string;
  organisationName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  pickupPostcode: string;
  deliveryPostcode: string;
  pickupAddress?: string;
  deliveryAddress?: string;
  pickupLat?: number;
  pickupLon?: number;
  deliveryLat?: number;
  deliveryLon?: number;
  dateRequired: string;
  pallets: number;
  weightKg: number;
  pricePerPallet: number;
  publishTo: string;
  notes?: string;
  createdAt: string;
}

const POSTED_JOBS_STORAGE_KEY = 'backloader-posted-jobs';

function getPostedJobs(): PostedJobEntry[] {
  try {
    const raw = localStorage.getItem(POSTED_JOBS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePostedJob(entry: Omit<PostedJobEntry, 'id' | 'createdAt'>) {
  const jobs = getPostedJobs();
  const newJob: PostedJobEntry = {
    ...entry,
    id: `posted-${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  jobs.unshift(newJob);
  localStorage.setItem(POSTED_JOBS_STORAGE_KEY, JSON.stringify(jobs));
  return newJob;
}

export default function Backloader() {
  // Default to a date that has mock jobs so the table is populated on load
  const [date, setDate] = useState('2026-02-27');
  const [returnStart, setReturnStart] = useState('');
  const [returnStartCoords, setReturnStartCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [returnEnd, setReturnEnd] = useState('');
  const [returnEndCoords, setReturnEndCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [capacityWeightTons, setCapacityWeightTons] = useState('');
  const [capacityPallets, setCapacityPallets] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 54.9783, lon: -1.6178, zoom: 8 });

  // Autocomplete
  const [startSuggestions, setStartSuggestions] = useState<typeof MOCK_LOCATIONS>([]);
  const [endSuggestions, setEndSuggestions] = useState<typeof MOCK_LOCATIONS>([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showEndSuggestions, setShowEndSuggestions] = useState(false);
  const autocompleteRef = useRef<NodeJS.Timeout | null>(null);

  // Subtab: 'browse' = main jobs view, 'post-job' = Post a job form (dedicated page)
  const [activeSubtab, setActiveSubtab] = useState<'browse' | 'post-job'>('browse');

  // Modals (Request a Job stays as modal)
  const [showRequestJob, setShowRequestJob] = useState(false);
  const [postJobSuccess, setPostJobSuccess] = useState(false);
  const [requestJobSuccess, setRequestJobSuccess] = useState(false);

  // Post Job form
  const [postOrganisationName, setPostOrganisationName] = useState('');
  const [postContactName, setPostContactName] = useState('');
  const [postContactEmail, setPostContactEmail] = useState('');
  const [postContactPhone, setPostContactPhone] = useState('');
  const [postPickupAddress, setPostPickupAddress] = useState('');
  const [postDeliveryAddress, setPostDeliveryAddress] = useState('');
  const [postPickupPostcode, setPostPickupPostcode] = useState('');
  const [postDeliveryPostcode, setPostDeliveryPostcode] = useState('');
  const [postDateRequired, setPostDateRequired] = useState('');
  const [postPallets, setPostPallets] = useState('');
  const [postWeightKg, setPostWeightKg] = useState('');
  const [postPricePerPallet, setPostPricePerPallet] = useState('');
  const [postPublishTo, setPostPublishTo] = useState('all');
  const selectedPartner = postPublishTo !== 'all' ? MOCK_PARTNER_COMPANIES.find((c) => c.name === postPublishTo) : null;
  const effectivePricePerPallet = selectedPartner
    ? selectedPartner.agreedPricePerPallet
    : parseFloat(postPricePerPallet) || 0;
  const [postNotes, setPostNotes] = useState('');
  const [postPickupSuggestions, setPostPickupSuggestions] = useState<typeof MOCK_LOCATIONS>([]);
  const [postDeliverySuggestions, setPostDeliverySuggestions] = useState<typeof MOCK_LOCATIONS>([]);
  const [showPostPickupSuggestions, setShowPostPickupSuggestions] = useState(false);
  const [showPostDeliverySuggestions, setShowPostDeliverySuggestions] = useState(false);
  const [postPickupLat, setPostPickupLat] = useState<number | undefined>();
  const [postPickupLon, setPostPickupLon] = useState<number | undefined>();
  const [postDeliveryLat, setPostDeliveryLat] = useState<number | undefined>();
  const [postDeliveryLon, setPostDeliveryLon] = useState<number | undefined>();
  const postAutocompleteRef = useRef<NodeJS.Timeout | null>(null);

  const [postedJobs, setPostedJobs] = useState<PostedJobEntry[]>([]);
  const [postedJobsLoaded, setPostedJobsLoaded] = useState(false);

  // Request Job form (when nothing found)
  const [reqReturnDate, setReqReturnDate] = useState('');
  const [reqStartLocation, setReqStartLocation] = useState('');
  const [reqCapacity, setReqCapacity] = useState('');
  const [reqReturnBase, setReqReturnBase] = useState('');
  const [reqNotes, setReqNotes] = useState('');
  const [reqSendTo, setReqSendTo] = useState('all');

  const searchSuggestions = useCallback(async (query: string, isEnd: boolean) => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      if (isEnd) setEndSuggestions([]);
      else setStartSuggestions([]);
      return;
    }
    const fromMock = MOCK_LOCATIONS.filter(
      (loc) =>
        loc.town.toLowerCase().includes(q) ||
        loc.postcode.toLowerCase().replace(/\s/g, '').includes(q.replace(/\s/g, ''))
    ).slice(0, 6);
    if (fromMock.length > 0) {
      if (isEnd) {
        setEndSuggestions(fromMock);
        setShowEndSuggestions(true);
      } else {
        setStartSuggestions(fromMock);
        setShowStartSuggestions(true);
      }
      return;
    }
    try {
      const url = API_ENDPOINTS.geocodeAutocomplete(query, 'GB', 6);
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        const list = (data.suggestions || []).slice(0, 6).map((s: GeocodeSuggestion) => ({
          address: s.address || s.postcode || query,
          postcode: s.postcode || '',
          town: (s.address || '').split(',')[0]?.trim() || s.postcode || '',
          lat: parseFloat(String(s.lat)),
          lon: parseFloat(String(s.lon)),
        }));
        if (isEnd) {
          setEndSuggestions(list);
          setShowEndSuggestions(true);
        } else {
          setStartSuggestions(list);
          setShowStartSuggestions(true);
        }
      }
    } catch {
      if (isEnd) setEndSuggestions([]);
      else setStartSuggestions([]);
    }
  }, []);

  const onStartInput = (value: string) => {
    setReturnStart(value);
    if (value.trim() === '') setReturnStartCoords(null);
    if (autocompleteRef.current) clearTimeout(autocompleteRef.current);
    autocompleteRef.current = setTimeout(() => searchSuggestions(value, false), 280);
  };

  const onEndInput = (value: string) => {
    setReturnEnd(value);
    if (autocompleteRef.current) clearTimeout(autocompleteRef.current);
    autocompleteRef.current = setTimeout(() => searchSuggestions(value, true), 280);
  };

  const pickStart = (loc: (typeof MOCK_LOCATIONS)[0]) => {
    setReturnStart(loc.address);
    setReturnStartCoords({ lat: loc.lat, lon: loc.lon });
    setShowStartSuggestions(false);
  };

  const pickEnd = (loc: (typeof MOCK_LOCATIONS)[0]) => {
    setReturnEnd(loc.address);
    setReturnEndCoords({ lat: loc.lat, lon: loc.lon });
    setShowEndSuggestions(false);
  };

  useEffect(() => {
    return () => {
      if (autocompleteRef.current) clearTimeout(autocompleteRef.current);
      if (postAutocompleteRef.current) clearTimeout(postAutocompleteRef.current);
    };
  }, []);

  const fetchPostSuggestions = useCallback(async (query: string, isDelivery: boolean) => {
    const q = query.trim();
    if (q.length < 2) {
      if (isDelivery) setPostDeliverySuggestions([]);
      else setPostPickupSuggestions([]);
      return;
    }
    const fromMock = MOCK_LOCATIONS.filter(
      (loc) =>
        loc.town.toLowerCase().includes(q.toLowerCase()) ||
        loc.postcode.toLowerCase().replace(/\s/g, '').includes(q.toLowerCase().replace(/\s/g, ''))
    ).slice(0, 6);
    if (fromMock.length > 0) {
      if (isDelivery) {
        setPostDeliverySuggestions(fromMock);
        setShowPostDeliverySuggestions(true);
      } else {
        setPostPickupSuggestions(fromMock);
        setShowPostPickupSuggestions(true);
      }
      return;
    }
    try {
      const url = API_ENDPOINTS.geocodeAutocomplete(query, 'GB', 6);
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        const list = (data.suggestions || [])
          .slice(0, 6)
          .map((s: GeocodeSuggestion) => ({
            address: s.address || s.postcode || query,
            postcode: s.postcode || '',
            town: (s.address || '').split(',')[0]?.trim() || s.postcode || '',
            lat: parseFloat(String(s.lat)),
            lon: parseFloat(String(s.lon)),
          }))
          .filter((loc: { lat: number; lon: number }) => !isNaN(loc.lat) && !isNaN(loc.lon));
        if (isDelivery) {
          setPostDeliverySuggestions(list);
          setShowPostDeliverySuggestions(true);
        } else {
          setPostPickupSuggestions(list);
          setShowPostPickupSuggestions(true);
        }
      } else {
        if (isDelivery) setPostDeliverySuggestions([]);
        else setPostPickupSuggestions([]);
      }
    } catch {
      if (isDelivery) setPostDeliverySuggestions([]);
      else setPostPickupSuggestions([]);
    }
  }, []);

  const onPostPickupInput = (value: string) => {
    setPostPickupAddress(value);
    if (postAutocompleteRef.current) clearTimeout(postAutocompleteRef.current);
    const q = value.trim();
    if (q.length < 2) {
      setPostPickupSuggestions([]);
      return;
    }
    postAutocompleteRef.current = setTimeout(() => {
      fetchPostSuggestions(value, false);
    }, 280);
  };

  const onPostDeliveryInput = (value: string) => {
    setPostDeliveryAddress(value);
    if (postAutocompleteRef.current) clearTimeout(postAutocompleteRef.current);
    const q = value.trim();
    if (q.length < 2) {
      setPostDeliverySuggestions([]);
      return;
    }
    postAutocompleteRef.current = setTimeout(() => {
      fetchPostSuggestions(value, true);
    }, 280);
  };

  const extractPostcodeFromAddress = (address: string): string => {
    const match = address.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/i);
    return match ? match[0].trim() : '';
  };

  // Load posted jobs from S3 (API) on mount; fallback to localStorage if API fails
  useEffect(() => {
    if (postedJobsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.backloaderPostedJobs());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && Array.isArray(data.jobs)) setPostedJobs(data.jobs);
      } catch {
        if (!cancelled) setPostedJobs(getPostedJobs());
      }
      if (!cancelled) setPostedJobsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [postedJobsLoaded]);

  const postedJobsAsBackloaderJobs = useMemo((): BackloaderJob[] => {
    return postedJobs.map((entry) => {
      const totalCost = entry.pallets * entry.pricePerPallet;
      const pickupPostcodeVal = (entry.pickupPostcode || entry.pickupAddress || '').trim();
      const deliveryPostcodeVal = (entry.deliveryPostcode || entry.deliveryAddress || '').trim();
      const pickupTown =
        (entry.pickupAddress?.split(',')[0]?.trim()) ||
        pickupPostcodeVal ||
        entry.pickupAddress ||
        '—';
      const deliveryTown =
        (entry.deliveryAddress?.split(',')[0]?.trim()) ||
        deliveryPostcodeVal ||
        entry.deliveryAddress ||
        '—';
      const jobDistanceMilesRaw =
        entry.pickupLat != null && entry.pickupLon != null && entry.deliveryLat != null && entry.deliveryLon != null
          ? haversineKm(entry.pickupLat, entry.pickupLon, entry.deliveryLat, entry.deliveryLon) * KM_TO_MILES
          : 0;
      const jobDistanceMiles = Math.round(jobDistanceMilesRaw * 10) / 10;
      return {
        id: entry.id,
        companyId: 'posted',
        companyName: entry.organisationName?.trim() || 'Your organisation',
        contactEmail: entry.contactEmail?.trim() || '',
        contactPhone: entry.contactPhone?.trim() || '',
        pickupTown,
        pickupPostcode: pickupPostcodeVal || pickupTown,
        pickupLat: entry.pickupLat ?? 54.0,
        pickupLon: entry.pickupLon ?? -1.5,
        deliveryTown,
        deliveryPostcode: deliveryPostcodeVal || deliveryTown,
        deliveryAddress: entry.deliveryAddress?.trim() || undefined,
        deliveryLat: entry.deliveryLat ?? 53.5,
        deliveryLon: entry.deliveryLon ?? -1.0,
        date: entry.dateRequired,
        pallets: entry.pallets,
        weightKg: entry.weightKg,
        pricePerPallet: entry.pricePerPallet,
        totalCost,
        jobDistanceMiles,
        notes: entry.notes?.trim() || undefined,
      };
    });
  }, [postedJobs]);

  const allJobs = useMemo(
    () => [...MOCK_BACKLOADER_JOBS, ...postedJobsAsBackloaderJobs],
    [postedJobsAsBackloaderJobs]
  );

  const filteredJobs = useMemo(() => {
    let list = allJobs.filter((j) => j.date === date);

    const weightTons = capacityWeightTons ? parseFloat(capacityWeightTons) : null;
    if (weightTons != null && !isNaN(weightTons)) {
      const maxKg = weightTons * 1000;
      list = list.filter((j) => j.weightKg <= maxKg);
    }
    const palletsCap = capacityPallets ? parseInt(capacityPallets, 10) : null;
    if (palletsCap != null && !isNaN(palletsCap)) {
      list = list.filter((j) => j.pallets <= palletsCap);
    }

    const startLat = returnStartCoords?.lat;
    const startLon = returnStartCoords?.lon;

    return list.map((job) => {
      const distanceKm =
        startLat != null && startLon != null
          ? haversineKm(startLat, startLon, job.pickupLat, job.pickupLon)
          : null;
      const distanceMiles = distanceKm != null ? distanceKm * KM_TO_MILES : null;
      return { ...job, distanceKm, distanceMiles };
    });
  }, [date, capacityWeightTons, capacityPallets, returnStartCoords, allJobs]);

  const tableData = useMemo(() => {
    const truncate = (text: string, maxW = '130px') => (
      <span className="block truncate max-w-33" style={{ maxWidth: maxW }} title={text}>{text}</span>
    );
    const weightTons = (kg: number) => (kg / 1000).toFixed(1);
    const costPerMileThresholds = { low: 4, high: 5.5 };
    return filteredJobs.map((job) => {
      const pickupStr = `${job.pickupTown} ${job.pickupPostcode}`;
      const deliveryStr = job.deliveryAddress ? `${job.deliveryTown} (${job.deliveryPostcode})` : `${job.deliveryTown} ${job.deliveryPostcode}`;
      const costPerMile = job.jobDistanceMiles > 0 ? job.totalCost / job.jobDistanceMiles : 0;
      const cpmClass =
        costPerMile >= costPerMileThresholds.high
          ? 'bg-success/20 text-success border border-success/40'
          : costPerMile < costPerMileThresholds.low
            ? 'bg-destructive/20 text-destructive border border-destructive/40'
            : 'bg-warning/20 text-warning border border-warning/40';
      return {
        id: job.id,
        company: truncate(job.companyName, '140px'),
        company_sortKey: job.companyName,
        distanceFromStart: job.distanceMiles != null ? `${job.distanceMiles.toFixed(1)} mi` : '–',
        distanceFromStart_sortKey: job.distanceMiles ?? 999999,
        pickupLocation: truncate(pickupStr, '150px'),
        deliveryTo: truncate(deliveryStr, '150px'),
        jobDistance: `${Number(job.jobDistanceMiles).toFixed(1)} mi`,
        jobDistance_sortKey: job.jobDistanceMiles,
        pallets: String(job.pallets),
        pallets_sortKey: job.pallets,
        weight: `${weightTons(job.weightKg)} T`,
        weight_sortKey: job.weightKg,
        totalCost: `£${job.totalCost.toLocaleString()}`,
        totalCost_sortKey: job.totalCost,
        costPerMile: (
          <span className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${cpmClass}`}>
            £{costPerMile > 0 ? costPerMile.toFixed(1) : '–'}/mi
          </span>
        ),
        costPerMile_sortKey: costPerMile,
        _job: job,
      };
    });
  }, [filteredJobs]);

  useEffect(() => {
    if (filteredJobs.length > 0) {
      const lats = filteredJobs.map((j) => j.pickupLat);
      const lons = filteredJobs.map((j) => j.pickupLon);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
      setMapCenter({ lat: centerLat, lon: centerLon, zoom: 8 });
    } else if (returnStartCoords) {
      setMapCenter({
        lat: returnStartCoords.lat,
        lon: returnStartCoords.lon,
        zoom: 10,
      });
    }
  }, [filteredJobs, returnStartCoords]);

  const handleRowClick = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    const job = filteredJobs.find((j) => j.id === jobId);
    if (job) {
      setMapCenter({ lat: job.pickupLat, lon: job.pickupLon, zoom: 12 });
    }
  }, [filteredJobs]);

  const resetPostForm = () => {
    setPostOrganisationName('');
    setPostContactName('');
    setPostContactEmail('');
    setPostContactPhone('');
    setPostPickupAddress('');
    setPostDeliveryAddress('');
    setPostPickupPostcode('');
    setPostDeliveryPostcode('');
    setPostPickupLat(undefined);
    setPostPickupLon(undefined);
    setPostDeliveryLat(undefined);
    setPostDeliveryLon(undefined);
    setPostDateRequired('');
    setPostPallets('');
    setPostWeightKg('');
    setPostPricePerPallet('');
    setPostNotes('');
    setShowPostPickupSuggestions(false);
    setShowPostDeliverySuggestions(false);
  };

  const handlePostJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pickupPc = postPickupPostcode.trim() || extractPostcodeFromAddress(postPickupAddress);
    const deliveryPc = postDeliveryPostcode.trim() || extractPostcodeFromAddress(postDeliveryAddress);
    const payload = {
      organisationName: postOrganisationName.trim() || undefined,
      contactName: postContactName.trim() || undefined,
      contactEmail: postContactEmail.trim() || undefined,
      contactPhone: postContactPhone.trim() || undefined,
      pickupPostcode: pickupPc || postPickupAddress.trim(),
      deliveryPostcode: deliveryPc || postDeliveryAddress.trim(),
      pickupAddress: postPickupAddress.trim() || undefined,
      deliveryAddress: postDeliveryAddress.trim() || undefined,
      pickupLat: postPickupLat,
      pickupLon: postPickupLon,
      deliveryLat: postDeliveryLat,
      deliveryLon: postDeliveryLon,
      dateRequired: postDateRequired || date,
      pallets: parseInt(postPallets, 10) || 0,
      weightKg: parseInt(postWeightKg, 10) || 0,
      pricePerPallet: effectivePricePerPallet || parseFloat(postPricePerPallet) || 0,
      publishTo: postPublishTo === 'all' ? 'All Transport Association partners' : postPublishTo,
      notes: postNotes.trim() || undefined,
    };
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.backloaderPostedJobs(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job: payload }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.jobs)) setPostedJobs(data.jobs);
      setPostJobSuccess(true);
      resetPostForm();
    } catch {
      savePostedJob(payload);
      setPostedJobs(getPostedJobs());
      setPostJobSuccess(true);
      resetPostForm();
    }
    setTimeout(() => {
      setPostJobSuccess(false);
      setActiveSubtab('browse');
    }, 1500);
  };

  const handleRequestJobSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Backload request: ${reqStartLocation} → ${reqReturnBase}`);
    const body = encodeURIComponent(
      `Return date: ${reqReturnDate}\nStart: ${reqStartLocation}\nAvailable capacity: ${reqCapacity}\nReturn base: ${reqReturnBase}\n\n${reqNotes}`
    );
    window.open(`mailto:?to=partners@transport-association.co.uk&subject=${subject}&body=${body}`, '_blank');
    setRequestJobSuccess(true);
    setReqReturnDate('');
    setReqStartLocation('');
    setReqCapacity('');
    setReqReturnBase('');
    setReqNotes('');
    setTimeout(() => {
      setShowRequestJob(false);
      setRequestJobSuccess(false);
    }, 1500);
  };

  const MapController = ({ selectedJobId: jid }: { selectedJobId: string | null }) => {
    const { map, isLoaded } = useMap();
    useEffect(() => {
      if (!isLoaded || !map || !jid) return;
      const job = filteredJobs.find((j) => j.id === jid);
      if (job) {
        map.flyTo({ center: [job.pickupLon, job.pickupLat], zoom: 12, duration: 800 });
      }
    }, [isLoaded, map, jid, filteredJobs]);
    return null;
  };

  const hasSearched = date !== '';
  const noResults = hasSearched && filteredJobs.length === 0;
  const hasReturnStart = Boolean(returnStartCoords);

  const subtabs = [
    { id: 'browse' as const, label: 'Browse jobs' },
    { id: 'post-job' as const, label: 'Post a job' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide-4 text-warning font-semibold">Backloader</p>
          <h1 className="text-2xl font-bold text-white mt-1">Plan your next backload</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Find return-leg jobs from trusted Transport Association partners.
          </p>
        </div>
        <Button
          type="button"
          variant="warning"
          size="md"
          onClick={() => setActiveSubtab('post-job')}
          className="hidden sm:inline-flex rounded-full font-semibold shadow-md shadow-warning/40"
        >
          <Plus className="w-4 h-4" aria-hidden />
          Post a job
        </Button>
      </div>

      {/* Subtab navigation */}
      <div className="flex gap-1 p-1 bg-card/80 rounded-xl border border-border w-fit">
        {subtabs.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant={activeSubtab === tab.id ? 'warning' : 'ghost'}
            size="md"
            onClick={() => setActiveSubtab(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Browse jobs: main search, map, table */}
      {activeSubtab === 'browse' && (
      <>
      {/* Where is your truck returning from – main input (replaces Return start) */}
      <div className="relative">
        <div className="bg-background/70 border border-accent rounded-2xl px-4 py-3 flex items-center gap-3 shadow-inner shadow-black/40 focus-within:border-warning/50 focus-within:ring-2 focus-within:ring-warning/20 transition-all">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-card border border-accent shrink-0">
            <MapPin className="w-5 h-5 text-warning" />
          </div>
          <input
            type="text"
            value={returnStart}
            onChange={(e) => onStartInput(e.target.value)}
            onFocus={() => (returnStart.length >= 2 || startSuggestions.length > 0) && setShowStartSuggestions(true)}
            onBlur={() => setTimeout(() => setShowStartSuggestions(false), 180)}
            placeholder="Where is your truck returning from?"
            className="flex-1 min-w-0 bg-transparent text-white placeholder-muted-foreground text-base focus:outline-none"
            aria-label="Return start location"
          />
          <Button
            type="button"
            variant="warning"
            size="icon"
            onClick={() => setActiveSubtab('post-job')}
            className="sm:hidden w-9 h-9 rounded-full"
            aria-label="Post a job"
          >
            <Plus className="w-4 h-4" aria-hidden />
          </Button>
        </div>
        {showStartSuggestions && startSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-2 left-0 right-0 bg-popover border border-accent rounded-xl shadow-xl max-h-56 overflow-auto">
            {startSuggestions.map((loc, i) => (
              <Button
                key={i}
                type="button"
                variant="ghost"
                size="md"
                onClick={() => pickStart(loc)}
                className="w-full h-auto justify-start gap-3 px-4 py-3 text-left text-foreground hover:bg-secondary/80 border-b border-border/50 last:border-0"
              >
                <MapPin className="w-4 h-4 text-warning shrink-0" aria-hidden />
                <span className="font-medium">{loc.town}</span>
                <span className="text-muted-foreground">{loc.postcode}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Options (date, return end, capacity) – only after user has set return start */}
      {hasReturnStart && (
        <div className="bg-card rounded-xl border border-border p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Calendar className="w-4 h-4" />
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-warning"
              />
            </div>

            <div className="space-y-2 relative">
              <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Navigation className="w-4 h-4" />
                Return end
              </label>
              <div className="relative">
              <input
                type="text"
                value={returnEnd}
                onChange={(e) => onEndInput(e.target.value)}
                onFocus={() => endSuggestions.length > 0 && setShowEndSuggestions(true)}
                onBlur={() => setTimeout(() => setShowEndSuggestions(false), 180)}
                placeholder="Town or postcode"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-warning"
              />
              {showEndSuggestions && endSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-popover border border-accent rounded-lg shadow-xl max-h-48 overflow-auto">
                  {endSuggestions.map((loc, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant="ghost"
                      size="md"
                      onClick={() => pickEnd(loc)}
                      className="w-full h-auto justify-start px-4 py-2 text-left text-foreground"
                    >
                      <span className="font-medium">{loc.town}</span>
                      <span className="text-muted-foreground ml-2">{loc.postcode}</span>
                    </Button>
                  ))}
                </div>
              )}
              </div>
            </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Truck className="w-4 h-4" />
              Pickup capacity (weight), tons
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={capacityWeightTons}
              onChange={(e) => setCapacityWeightTons(e.target.value)}
              placeholder="e.g. 3.5"
              className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-warning"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Package className="w-4 h-4" />
              Pickup capacity (pallets)
            </label>
            <input
              type="number"
              min="0"
              value={capacityPallets}
              onChange={(e) => setCapacityPallets(e.target.value)}
              placeholder="e.g. 10"
              className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-warning"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border flex-wrap gap-3">
          <span className="text-sm text-muted-foreground">
              Showing <span className="font-semibold text-white">{filteredJobs.length}</span> jobs
              {date && (
                <span className="ml-2 text-muted-foreground">
                  for {new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </span>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={() => {
              setReturnStart('');
              setReturnEnd('');
              setReturnStartCoords(null);
              setReturnEndCoords(null);
              setCapacityWeightTons('');
              setCapacityPallets('');
            }}
            className="text-muted-foreground hover:text-white"
          >
            Clear
          </Button>
        </div>
      </div>
      )}

      {/* Map then Table */}
      <div className="space-y-6">
        <div
          className="bg-card rounded-xl border border-border overflow-visible relative z-10"
          style={{ height: '420px', minHeight: '360px' }}
          data-map-container
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/70 bg-card/80 backdrop-blur-sm rounded-t-xl overflow-hidden">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-warning" />
              <span className="text-sm font-medium text-white">Route & jobs on map</span>
            </div>
            {filteredJobs.length > 0 && (
              <span className="text-xs text-muted-foreground">
                Showing {filteredJobs.length} pickup location{filteredJobs.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <MapComponent
            center={[mapCenter.lon, mapCenter.lat]}
            zoom={mapCenter.zoom}
            theme="dark"
          >
            <MapController selectedJobId={selectedJobId} />
            <MapControls />
            {returnStartCoords && (
              <MapMarker longitude={returnStartCoords.lon} latitude={returnStartCoords.lat}>
                <MarkerContent>
                  <div className="bg-ring text-white px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg shadow-ring/40">
                    <MapPin className="w-3 h-3" />
                    Start
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">Return start</div>
                  <div className="text-xs text-muted-foreground">{returnStart}</div>
                </MarkerTooltip>
              </MapMarker>
            )}
            {returnEndCoords && (
              <MapMarker longitude={returnEndCoords.lon} latitude={returnEndCoords.lat}>
                <MarkerContent>
                  <div className="bg-destructive text-white px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg shadow-destructive/40">
                    <Navigation className="w-3 h-3" />
                    End
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">Return end</div>
                  <div className="text-xs text-muted-foreground">{returnEnd}</div>
                </MarkerTooltip>
              </MapMarker>
            )}
            {filteredJobs.map((job) => (
              <MapMarker
                key={job.id}
                longitude={job.pickupLon}
                latitude={job.pickupLat}
                onClick={() => setSelectedJobId(job.id)}
              >
                <MarkerContent>
                  <div
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                      selectedJobId === job.id
                        ? 'bg-warning text-black ring-2 ring-warning shadow-lg shadow-warning/40'
                        : 'bg-success text-white hover:bg-success shadow-md shadow-success/40'
                    }`}
                  >
                    <Package className="w-3 h-3 inline mr-1" />
                    {job.pallets} pal
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">{job.companyName}</div>
                  <div className="text-xs text-muted-foreground">{job.pickupTown} {job.pickupPostcode}</div>
                  <div className="text-xs text-warning mt-1">£{job.totalCost.toLocaleString()} · {Number(job.jobDistanceMiles).toFixed(1)} mi</div>
                  <span className="text-xs text-muted-foreground mt-1 block">Click for details</span>
                </MarkerTooltip>
                <MarkerPopup closeButton className="min-w-65 bg-popover border border-accent text-left p-0">
                  <div className="p-4 text-sm">
                    <div className="font-semibold text-white border-b border-accent pb-2 mb-2">{job.companyName}</div>
                    <div className="space-y-1.5 text-muted-foreground">
                      <div>
                        <span className="text-muted-foreground">Pickup:</span>{' '}
                        {job.pickupTown} {job.pickupPostcode}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Delivery:</span>{' '}
                        {job.deliveryAddress ? `${job.deliveryTown} (${job.deliveryPostcode})` : `${job.deliveryTown} ${job.deliveryPostcode}`}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Pallets:</span> {job.pallets} · <span className="text-muted-foreground">Weight:</span> {(job.weightKg / 1000).toFixed(1)} T
                      </div>
                      <div>
                        <span className="text-muted-foreground">Job distance:</span> {Number(job.jobDistanceMiles).toFixed(1)} mi · <span className="text-warning font-medium">£{job.totalCost.toLocaleString()}</span>
                      </div>
                      {job.notes && (
                        <div>
                          <span className="text-muted-foreground">Notes:</span> {job.notes}
                        </div>
                      )}
                    </div>
                    {(job.contactPhone || job.contactEmail) && (
                      <div className="mt-3 pt-3 border-t border-accent">
                        <div className="text-muted-foreground text-xs mb-1">Contact</div>
                        {job.contactPhone && (
                          <a href={`tel:${job.contactPhone}`} className="flex items-center gap-1.5 text-warning hover:text-warning">
                            <Phone className="w-3.5 h-3.5" />
                            {job.contactPhone}
                          </a>
                        )}
                        {job.contactEmail && (
                          <a href={`mailto:${job.contactEmail}`} className="flex items-center gap-1.5 text-warning hover:text-warning mt-1">
                            <Mail className="w-3.5 h-3.5" />
                            {job.contactEmail}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </MarkerPopup>
              </MapMarker>
            ))}
          </MapComponent>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {tableData.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-14 h-14 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">No jobs match your criteria</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {noResults
                  ? 'Try a different date or capacity, or request a job from partners.'
                  : 'Set a date and optional filters to see available jobs.'}
              </p>
              {noResults && (
                <Button
                  type="button"
                  variant="warning"
                  size="md"
                  onClick={() => setShowRequestJob(true)}
                >
                  <Send className="w-4 h-4" aria-hidden />
                  Request a Job
                </Button>
              )}
            </div>
          ) : (
            <div className="max-h-150 overflow-y-auto">
              <DataTable
                data={tableData}
                columns={[
                  { key: 'company', label: 'Company' },
                  { key: 'distanceFromStart', label: 'Distance from start', align: 'right' },
                  { key: 'pickupLocation', label: 'Pickup location' },
                  { key: 'deliveryTo', label: 'Delivery to' },
                  { key: 'jobDistance', label: 'Job distance', align: 'right' },
                  { key: 'pallets', label: 'Pallets', align: 'center' },
                  { key: 'weight', label: 'Weight', align: 'right' },
                  { key: 'costPerMile', label: 'Cost per mile', align: 'right' },
                  { key: 'totalCost', label: 'Total job cost', align: 'right' },
                ]}
                customColumnWidths={{
                  company: { minWidth: 10, maxWidth: 14 },
                  pickupLocation: { minWidth: 10, maxWidth: 14 },
                  deliveryTo: { minWidth: 10, maxWidth: 14 },
                  distanceFromStart: { minWidth: 7, maxWidth: 10 },
                  jobDistance: { minWidth: 6, maxWidth: 9 },
                  pallets: { minWidth: 5, maxWidth: 6 },
                  weight: { minWidth: 5, maxWidth: 7 },
                  costPerMile: { minWidth: 7, maxWidth: 10 },
                  totalCost: { minWidth: 7, maxWidth: 10 },
                }}
                defaultSort={{ column: 'distanceFromStart', direction: 'asc' }}
                maxRows={12}
                hoverColor="yellow"
                onRowClick={(row) => row._job && handleRowClick((row._job as { id: string }).id)}
                selectedRowId={selectedJobId}
                rowIdKey="id"
              />
            </div>
          )}
        </div>
      </div>

      {/* Contact strip when a row is selected */}
      {selectedJobId && (() => {
        const job = filteredJobs.find((j) => j.id === selectedJobId);
        if (!job) return null;
        return (
          <div className="bg-card rounded-xl border border-border p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Building2 className="w-5 h-5 text-warning" />
              <span className="font-medium text-white">{job.companyName}</span>
              {job.contactEmail && (
                <a
                  href={`mailto:${job.contactEmail}`}
                  className="inline-flex items-center gap-1.5 text-sm text-warning hover:text-warning"
                >
                  <Mail className="w-4 h-4" />
                  {job.contactEmail}
                </a>
              )}
              {job.contactPhone && (
                <a
                  href={`tel:${job.contactPhone}`}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white"
                >
                  <Phone className="w-4 h-4" />
                  {job.contactPhone}
                </a>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={() => setSelectedJobId(null)}
              aria-label="Close"
            >
              <X className="w-5 h-5" aria-hidden />
            </Button>
          </div>
        );
      })()}
      </>
      )}

      {/* Post a Job – dedicated subtab (full page form) */}
      {activeSubtab === 'post-job' && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-bold text-white">Post a Job to Partners</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Share your return-leg job with Transport Association partners. You can expand this form as needed.
            </p>
          </div>
          <form onSubmit={handlePostJobSubmit} className="p-6 space-y-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Post to</label>
              <select
                value={postPublishTo}
                onChange={(e) => {
                  const v = e.target.value;
                  setPostPublishTo(v);
                  const partner = MOCK_PARTNER_COMPANIES.find((c) => c.name === v);
                  if (partner) setPostPricePerPallet(String(partner.agreedPricePerPallet));
                  else setPostPricePerPallet('');
                }}
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
              >
                <option value="all">All partners (set your own rate)</option>
                {MOCK_PARTNER_COMPANIES.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name} — £{c.agreedPricePerPallet}/pallet
                  </option>
                ))}
              </select>
              {selectedPartner && (
                <p className="mt-1.5 text-xs text-warning">
                  Pre-agreed rate: £{selectedPartner.agreedPricePerPallet} per pallet
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Your organisation <span className="text-muted-foreground font-normal">(optional)</span></label>
              <input
                type="text"
                value={postOrganisationName}
                onChange={(e) => setPostOrganisationName(e.target.value)}
                placeholder="e.g. Logistics Ltd"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-muted-foreground mb-1">Pickup location</label>
              <input
                type="text"
                value={postPickupAddress}
                onChange={(e) => onPostPickupInput(e.target.value)}
                onFocus={() => postPickupSuggestions.length > 0 && setShowPostPickupSuggestions(true)}
                onBlur={() => setTimeout(() => setShowPostPickupSuggestions(false), 180)}
                placeholder="Type town or postcode for suggestions"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
              />
              {showPostPickupSuggestions && postPickupSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-popover border border-accent rounded-lg shadow-xl max-h-40 overflow-auto">
                  {postPickupSuggestions.map((loc, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant="ghost"
                      size="md"
                      onClick={() => {
                        setPostPickupAddress(loc.address);
                        setPostPickupPostcode(loc.postcode);
                        setPostPickupLat(loc.lat);
                        setPostPickupLon(loc.lon);
                        setShowPostPickupSuggestions(false);
                      }}
                      className="w-full h-auto justify-start px-4 py-2 text-left text-foreground"
                    >
                      <span className="font-medium">{loc.town}</span>
                      <span className="text-muted-foreground ml-2">{loc.postcode}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-muted-foreground mb-1">Delivery location</label>
              <input
                type="text"
                value={postDeliveryAddress}
                onChange={(e) => onPostDeliveryInput(e.target.value)}
                onFocus={() => postDeliverySuggestions.length > 0 && setShowPostDeliverySuggestions(true)}
                onBlur={() => setTimeout(() => setShowPostDeliverySuggestions(false), 180)}
                placeholder="Type town or postcode for suggestions"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
              />
              {showPostDeliverySuggestions && postDeliverySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-popover border border-accent rounded-lg shadow-xl max-h-40 overflow-auto">
                  {postDeliverySuggestions.map((loc, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant="ghost"
                      size="md"
                      onClick={() => {
                        setPostDeliveryAddress(loc.address);
                        setPostDeliveryPostcode(loc.postcode);
                        setPostDeliveryLat(loc.lat);
                        setPostDeliveryLon(loc.lon);
                        setShowPostDeliverySuggestions(false);
                      }}
                      className="w-full h-auto justify-start px-4 py-2 text-left text-foreground"
                    >
                      <span className="font-medium">{loc.town}</span>
                      <span className="text-muted-foreground ml-2">{loc.postcode}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Date required</label>
              <input
                type="date"
                value={postDateRequired || date}
                onChange={(e) => setPostDateRequired(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Pallets</label>
                <input
                  type="number"
                  min="1"
                  value={postPallets}
                  onChange={(e) => setPostPallets(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Weight (kg)</label>
                <input
                  type="number"
                  min="1"
                  value={postWeightKg}
                  onChange={(e) => setPostWeightKg(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
            </div>
            {postPublishTo === 'all' && (
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Price per pallet (£)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={postPricePerPallet}
                  onChange={(e) => setPostPricePerPallet(e.target.value)}
                  placeholder="e.g. 95"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
            )}
            <div className="border-t border-accent pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">Contact details (optional)</p>
              <p className="text-xs text-muted-foreground mb-3">Include your details so partners can get in touch about this job.</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={postContactName}
                  onChange={(e) => setPostContactName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning placeholder-muted-foreground"
                />
                <input
                  type="email"
                  value={postContactEmail}
                  onChange={(e) => setPostContactEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning placeholder-muted-foreground"
                />
                <input
                  type="tel"
                  value={postContactPhone}
                  onChange={(e) => setPostContactPhone(e.target.value)}
                  placeholder="Phone"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning placeholder-muted-foreground"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Notes (optional)</label>
              <textarea
                value={postNotes}
                onChange={(e) => setPostNotes(e.target.value)}
                rows={2}
                placeholder="Any special instructions"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setActiveSubtab('browse')}
                className="text-muted-foreground hover:text-white"
              >
                Back to browse
              </Button>
              <Button
                type="submit"
                variant="warning"
                size="md"
                disabled={postJobSuccess}
              >
                {postJobSuccess ? 'Posted' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Request a Job modal */}
      {showRequestJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => !requestJobSuccess && setShowRequestJob(false)}>
          <div
            className="bg-card rounded-xl border border-accent shadow-2xl w-full max-w-lg max-h-screen-90 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Request a Job (when nothing is published)</h2>
              <Button type="button" variant="ghost" size="iconSm" onClick={() => setShowRequestJob(false)} aria-label="Close">
                <X className="w-5 h-5" aria-hidden />
              </Button>
            </div>
            <form onSubmit={handleRequestJobSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Return date</label>
                <input
                  type="date"
                  value={reqReturnDate || date}
                  onChange={(e) => setReqReturnDate(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Start location</label>
                <input
                  type="text"
                  value={reqStartLocation || returnStart}
                  onChange={(e) => setReqStartLocation(e.target.value)}
                  placeholder="e.g. Newcastle"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Available capacity</label>
                <input
                  type="text"
                  value={reqCapacity}
                  onChange={(e) => setReqCapacity(e.target.value)}
                  placeholder="e.g. 10 pallets, 2.5t"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Return base</label>
                <input
                  type="text"
                  value={reqReturnBase || returnEnd}
                  onChange={(e) => setReqReturnBase(e.target.value)}
                  placeholder="e.g. Leeds"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Brief notes</label>
                <textarea
                  value={reqNotes}
                  onChange={(e) => setReqNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Looking for 10 pallets heading south from Newcastle"
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Send to</label>
                <select
                  value={reqSendTo}
                  onChange={(e) => setReqSendTo(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:ring-2 focus:ring-warning"
                >
                  <option value="all">All partners</option>
                  {MOCK_PARTNER_COMPANIES.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" size="md" onClick={() => setShowRequestJob(false)} className="text-muted-foreground hover:text-white">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="warning"
                  size="md"
                  disabled={requestJobSuccess}
                >
                  {requestJobSuccess ? 'Sent' : 'Send (mailto)'}
                  <Send className="w-4 h-4" aria-hidden />
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
